/**
 * Client for the tenant's customer-class crosswalk — /:customer/data-model/class-crosswalk on tri-server.
 * A customer class (the customer's own asset classification, e.g. Bosch trade + class code) stands for a
 * DIN 276 node of three digits (the asset may refine inside the cost group) or five digits (fixed).
 * Reading is open to the tenant, writing needs an administrator of it.
 */

import { configuration } from "../../../Configuration";
import { ApplicationError, UnauthorizedError } from "../../../general/Errors";
import { authenticatedFetch } from "../../../JWTAuthService";

/** Where an entry comes from: derived by our pipeline, entered by a person, or accepted by a person. */
export type CrosswalkSource = "derived" | "manual" | "confirmed";

export interface ClassCrosswalkEntry {
  _id: string;
  key: string;
  scheme: string;
  tradeCode?: string;
  tradeLabel?: string;
  classCode: string;
  classLabel?: string;
  /** Set on a pair entry (class × component); it wins over the class entry. */
  componentCode?: string;
  componentLabel?: string;
  din276Code: string;
  source: CrosswalkSource;
  note?: string;
  updatedBy?: string;
  updatedAt?: string;
  /** Read-only, computed by the server. */
  din276Label?: string | null;
  regulatedObjectCode?: string | null;
  regulatedObjectName?: string | null;
  obligationCount?: number;
  assetCount?: number;
}

/** What a person enters or changes. */
export interface ClassCrosswalkDraft {
  scheme: string;
  tradeCode?: string;
  tradeLabel?: string;
  classCode: string;
  classLabel?: string;
  /** Set on a pair entry (class × component); it wins over the class entry. */
  componentCode?: string;
  componentLabel?: string;
  din276Code: string;
  note?: string;
}

/** Three digits or a five-digit node — the rule the server applies too. */
export const DIN276_CODE_PATTERN = /^\d{3}(\.\d{2,3})?$/;

const processResponse = async (response: Response) => {
  if (response.ok) return response.json();
  if (response.status === 401) throw new UnauthorizedError();
  let errorMessage = `${response.status} ${response.statusText}`;
  try {
    const errorData = await response.json();
    errorMessage = errorData.message || errorData.error || errorMessage;
  } catch {
    // response body not JSON — keep default message
  }
  throw new ApplicationError(errorMessage);
};

const idOf = (value: any): string => String(value?.$oid ?? value);
const normalizeEntry = (raw: any): ClassCrosswalkEntry => ({ ...raw, _id: idOf(raw._id) });

export const useClassCrosswalkAPI = (customer: string) => {
  const base = `${configuration.serverUrl}/${customer}/data-model/class-crosswalk`;

  const send = async (url: string, method: "GET" | "POST" | "PUT" | "DELETE", body?: unknown) =>
    processResponse(
      await authenticatedFetch(url, {
        method,
        body: body !== undefined ? JSON.stringify(body) : undefined,
        headers: { "Content-Type": "application/json" },
      }),
    );

  return {
    getEntries: async (): Promise<ClassCrosswalkEntry[]> => ((await send(base, "GET"))?.entries ?? []).map(normalizeEntry),
    createEntry: async (draft: ClassCrosswalkDraft): Promise<ClassCrosswalkEntry> =>
      normalizeEntry((await send(base, "POST", draft))?.entry),
    updateEntry: async (id: string, draft: Partial<ClassCrosswalkDraft>, confirm = false): Promise<ClassCrosswalkEntry> =>
      normalizeEntry((await send(`${base}/${id}`, "PUT", { ...draft, confirm }))?.entry),
    confirmEntries: async (ids: string[]): Promise<number> => (await send(`${base}/confirm`, "POST", { ids }))?.confirmed ?? 0,
    deleteEntry: async (id: string): Promise<number> => (await send(`${base}/${id}`, "DELETE"))?.deleted ?? 0,
  };
};

/** Filter of the table: free text over trade, class and DIN 276, and the source. */
export interface CrosswalkFilter {
  text: string;
  source: CrosswalkSource | "all";
}

export const EMPTY_CROSSWALK_FILTER: CrosswalkFilter = { text: "", source: "all" };

export const filterCrosswalkEntries = (entries: ClassCrosswalkEntry[], filter: CrosswalkFilter): ClassCrosswalkEntry[] => {
  const needle = filter.text.trim().toLocaleLowerCase("de");
  return entries.filter((entry) => {
    if (filter.source !== "all" && entry.source !== filter.source) return false;
    if (!needle) return true;
    return [entry.tradeCode, entry.tradeLabel, entry.classCode, entry.classLabel, entry.componentCode, entry.componentLabel, entry.din276Code, entry.din276Label, entry.regulatedObjectName]
      .filter(Boolean)
      .some((value) => String(value).toLocaleLowerCase("de").includes(needle));
  });
};
